import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FaArrowLeft,
  FaClock,
  FaUserGraduate,
  FaCheckCircle,
  FaCalendarAlt,
  FaChalkboardTeacher,
} from 'react-icons/fa';
import { getCourse } from '../constants/courses';

const CourseModal = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const course = getCourse(id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const backToCourses = () => navigate('/', { state: { scrollTo: 'courses' } });

  if (!course) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 pt-20">
        <div className="text-center">
          <p className="eyebrow">404</p>
          <h1 className="section-title">Course not found</h1>
          <p className="section-lead">That course either moved or never existed.</p>
          <button onClick={backToCourses} className="btn-primary mt-8">
            <FaArrowLeft className="h-3 w-3" />
            Back to all courses
          </button>
        </div>
      </div>
    );
  }

  const { title, Icon, description, details, highlights, durationLong, level, price, schedule, instructor } = course;

  const facts = [
    { label: 'Duration', value: durationLong, icon: FaClock },
    { label: 'Level', value: level, icon: FaUserGraduate },
    { label: 'Instructor', value: instructor, icon: FaChalkboardTeacher },
    { label: 'Schedule', value: schedule, icon: FaCalendarAlt },
  ];

  return (
    <div className="min-h-screen pt-24 md:pt-32">
      <div className="container-page pb-24">
        <button onClick={backToCourses} className="btn-ghost mb-10 text-sm">
          <FaArrowLeft className="h-3 w-3" />
          All courses
        </button>

        {/* Header */}
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="max-w-2xl">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg border border-surface-line bg-surface-raised text-brand-400">
              <Icon className="h-5 w-5" />
            </span>
            <h1 className="mt-6 text-3xl font-semibold text-ink md:text-4xl">{title}</h1>
            <p className="section-lead">{description}</p>
          </div>

          <div className="card w-full shrink-0 p-6 md:w-64">
            <p className="text-sm text-ink-faint">Course fee</p>
            <p className="mt-1 text-3xl font-semibold text-ink">{price}</p>
            <button
              onClick={() => navigate('/', { state: { scrollTo: 'contact' } })}
              className="btn-primary mt-5 w-full"
            >
              Enroll Now
            </button>
            <p className="mt-3 text-center text-xs text-ink-faint">
              We&rsquo;ll get back to you within a day
            </p>
          </div>
        </div>

        {/* Facts */}
        <div className="mt-14 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {facts.map(({ label, value, icon: FactIcon }) => (
            <div key={label} className="card p-5">
              <div className="flex items-center gap-2 text-ink-faint">
                <FactIcon className="h-3.5 w-3.5" />
                <span className="text-xs font-medium uppercase tracking-[0.14em]">{label}</span>
              </div>
              <p className="mt-2 text-sm font-medium text-ink">{value}</p>
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="mt-16 grid gap-12 md:grid-cols-2">
          <section>
            <h2 className="text-xl font-semibold text-ink">What you will learn</h2>
            <div className="rule mt-4" />
            <ul className="mt-6 space-y-4">
              {details.map((detail) => (
                <li key={detail} className="flex items-start text-sm text-ink-muted">
                  <FaCheckCircle className="mr-3 mt-0.5 h-4 w-4 flex-shrink-0 text-brand-500" />
                  {detail}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-ink">Course highlights</h2>
            <div className="rule mt-4" />
            <ul className="mt-6 space-y-3">
              {highlights.map((highlight) => (
                <li key={highlight} className="card flex items-center p-4 text-sm text-ink-muted">
                  <FaCheckCircle className="mr-3 h-4 w-4 flex-shrink-0 text-brand-500" />
                  {highlight}
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default CourseModal;
